import React, {useState, useEffect, useCallback} from "react";
import { useTranslation } from "react-i18next";
import {useFormContext} from "react-hook-form";
import {useDispatch} from "react-redux";

import Button from "@mui/material/Button";
import { Edit as EditIcon } from '@vedmarket/ui-kit';

import Notification, {NOTIFICATION_TYPE} from "../../../../../../../../../../../../common/notification";
import { getProductById } from "../../../../../../../../helpers";
import {
  useInvoices,
  useSuggestAdditionalFieldsMutation,
  useUpdateProductById,
} from "../../../../../../../../../../../../../api/calc";
import ModalBig from "../../../../../../../../../../../../common/modalBig";

import {ENGINE_TYPE_OPTIONS, isVehicleHsCode, PACKAGE_FORM, PackageForm} from '../add_form/helpers';
import ExciseFields from "../excise_fields";
import { Completed } from '@vedmarket/ui-kit';
import MenuButton from '../../../../../../../../../../../../common/menuButton';
import {useDemoExcise} from "components/cabinet/demo/steps/packaging/use-product-form";

type Props = {
  data?: Excise[]
  id?: number
  invoiceId?: number
  insideModal?: boolean
}

const normalizeExciseRate = (
  rate: Excise | NonNullable<ProductParamsOutput['excise_rate']> | undefined,
): SuggestAdditionalFieldsInput['excise_rate'] => {
  if (!rate) {
    return null;
  }
  if ('Value' in rate) {
    return {
      description: rate.Condition ?? '',
      document: rate.Document ?? '',
      unit: rate.Measure?.Razm ?? '',
      value: String(rate.Value ?? ''),
    };
  }
  return {
    description: rate.description ?? '',
    document: rate.document ?? '',
    unit: rate.unit ?? '',
    value: String(rate.value ?? ''),
  };
};

const Excise = ({data, id, invoiceId, insideModal} : Props) => {

  const demoPlayback = useDemoExcise()

  const { t } = useTranslation()

  const { getValues } = useFormContext<PackageForm>()

  const invoices = useInvoices();

  const product = getProductById(invoices, invoiceId, id);

  const { getValues: getValuesPackageForm, setValue: setValuePackageForm } = useFormContext<PackageForm>()

  const isVehicle = isVehicleHsCode(getValuesPackageForm(PACKAGE_FORM.CODE) || product?.code);

  const vehicleIsCompleted = Boolean(
    (product?.engine_type || getValuesPackageForm(PACKAGE_FORM.ENGINE_TYPE)) &&
    (product?.engine_power_hp || getValuesPackageForm(PACKAGE_FORM.ENGINE_POWER_HP))
  );

  const exciseIsCompleted = () =>
    Boolean(product?.excise_value) || Boolean(getValuesPackageForm(PACKAGE_FORM.EXCISE_VALUE)) || product?.excise_value === 0 || Boolean(getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE));

  const [ isCompletedAdditional, setIsCompletedAdditional ] = useState(isVehicle ? vehicleIsCompleted : exciseIsCompleted());

  const [ openModalAddFields, setOpenModalAddFields ] = useState(false);
  const [ prefillSuggestions, setPrefillSuggestions ] = useState<SuggestAdditionalFieldsOutput | null>(null);

  const dispatch = useDispatch()

  const [ suggestAdditionalFields, { isLoading: prefillLoading } ] = useSuggestAdditionalFieldsMutation();

  useEffect(() => {
    setIsCompletedAdditional(isVehicle ? vehicleIsCompleted : exciseIsCompleted());
  }, [ product, getValuesPackageForm(PACKAGE_FORM.EXCISE_VALUE), getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE), vehicleIsCompleted, isVehicle ])

  const applySuggestionsToForm = useCallback((suggestions: SuggestAdditionalFieldsOutput) => {
    if (suggestions.engine_type) {
      setValuePackageForm(PACKAGE_FORM.ENGINE_TYPE, suggestions.engine_type);
    }
    if (suggestions.engine_power_hp != null) {
      setValuePackageForm(PACKAGE_FORM.ENGINE_POWER_HP, suggestions.engine_power_hp);
    }
    if (suggestions.engine_volume_cc != null) {
      setValuePackageForm(PACKAGE_FORM.ENGINE_VOLUME_CC, suggestions.engine_volume_cc);
    }
    if (suggestions.manufacture_date) {
      setValuePackageForm(PACKAGE_FORM.MANUFACTURE_DATE, suggestions.manufacture_date);
    }
    if (suggestions.excise_value != null) {
      setValuePackageForm(PACKAGE_FORM.EXCISE_VALUE, suggestions.excise_value);
    }
  }, [ setValuePackageForm ]);

  const handleOpenModal = async () => {
    setOpenModalAddFields(true);
    setPrefillSuggestions(null);

    if (!invoiceId) {
      return;
    }

    const normalizedExciseRate = normalizeExciseRate(
      getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE) || product?.excise_rate,
    );

    try {
      const suggestions = await suggestAdditionalFields({
        invoiceId,
        code: getValuesPackageForm(PACKAGE_FORM.CODE) || product?.code || '',
        name: getValuesPackageForm(PACKAGE_FORM.NAME) || product?.name || '',
        description: getValuesPackageForm(PACKAGE_FORM.DESCRIPTION) || product?.description || '',
        engine_type: getValuesPackageForm(PACKAGE_FORM.ENGINE_TYPE) ?? product?.engine_type,
        engine_power_hp: getValuesPackageForm(PACKAGE_FORM.ENGINE_POWER_HP) ?? product?.engine_power_hp,
        engine_volume_cc: getValuesPackageForm(PACKAGE_FORM.ENGINE_VOLUME_CC) ?? product?.engine_volume_cc,
        manufacture_date: getValuesPackageForm(PACKAGE_FORM.MANUFACTURE_DATE) ?? product?.manufacture_date,
        excise_value: getValuesPackageForm(PACKAGE_FORM.EXCISE_VALUE) ?? product?.excise_value,
        excise_rate: normalizedExciseRate,
        excise_options: data ?? [],
      }).unwrap();

      applySuggestionsToForm(suggestions);
      setPrefillSuggestions(suggestions);
    } catch {
      setPrefillSuggestions({});
    }
  };

  const handleCloseModal = () => {
    setOpenModalAddFields(false);
    setPrefillSuggestions(null);
  };

  const getVehicleInfo = () => {
    const engineType = getValuesPackageForm(PACKAGE_FORM.ENGINE_TYPE) || product?.engine_type;
    const enginePower = getValuesPackageForm(PACKAGE_FORM.ENGINE_POWER_HP) || product?.engine_power_hp;
    const engineVolume = getValuesPackageForm(PACKAGE_FORM.ENGINE_VOLUME_CC) || product?.engine_volume_cc;
    const manufactureDate = getValuesPackageForm(PACKAGE_FORM.MANUFACTURE_DATE) || product?.manufacture_date;

    const engineTypeLabel = ENGINE_TYPE_OPTIONS.find((option) => option.value === engineType)?.labelKey;

    return [
      ...(engineTypeLabel ? [ {
        label: t('calc__text__packaging__engineType'),
        value: t(engineTypeLabel),
        isStrong: true,
      } ] : []),
      ...(enginePower ? [ {
        label: t('calc__text__packaging__enginePower'),
        value: `${enginePower}`,
      } ] : []),
      ...(engineVolume ? [ {
        label: t('calc__text__packaging__engineVolume'),
        value: `${engineVolume}`,
      } ] : []),
      ...(manufactureDate ? [ {
        label: t('calc__text__packaging__manufactureDate'),
        value: `${manufactureDate}`,
      } ] : []),
    ]
  }

  const getInfo = () => {

    if (isVehicle) {
      return getVehicleInfo()
    }

    if (!product && !getValuesPackageForm(PACKAGE_FORM.EXCISE_VALUE) && !getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE)) {
      return []
    }

    if (product) {

      return [
        {
          label: t('common__text__chosenValue'),
          // @ts-ignore
          value: `${product.excise_rate?.value} ${(product.excise_rate?.description && product.excise_rate?.value !== 0) ? `${product.excise_rate?.description}.` : ""} ${product.excise_rate?.document || ""}`,
          isStrong: true,
        },
        ...(product.excise_value
          ? [ {
            label: t('calc__text__packaging__countsAndUnits'),
            value: `${product.excise_value} ${product.excise_rate?.unit || ""}`,
          } ]
          : [])
      ]
    }

    return [
      {
        label: t('common__text__chosenValue'),
        // @ts-ignore
        value: `${getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE)?.Value || getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE)?.value} ${getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE)?.Condition || (getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE)?.description !== '0' ? getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE)?.description : '') || ''}. ${getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE)?.Document || getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE)?.document || ""}`,
        isStrong: true,
      },
      ...(getValuesPackageForm(PACKAGE_FORM.EXCISE_VALUE)
        ? [ {
          label: t('calc__text__packaging__countsAndUnits'),
          // @ts-ignore
          value: `${getValuesPackageForm(PACKAGE_FORM.EXCISE_VALUE)} ${getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE)?.Measure?.Razm || getValuesPackageForm(PACKAGE_FORM.EXCISE_RATE)?.unit}`,
        } ]
        : [])
    ]
  };

  const handleRemove = () => {

    const data = {
      ...product,
      [PACKAGE_FORM.EXCISE_VALUE]: "",
      [PACKAGE_FORM.EXCISE_RATE]: {},
      ...(isVehicle && {
        [PACKAGE_FORM.ENGINE_TYPE]: null,
        [PACKAGE_FORM.ENGINE_POWER_HP]: null,
        [PACKAGE_FORM.ENGINE_VOLUME_CC]: null,
        [PACKAGE_FORM.MANUFACTURE_DATE]: null,
      }),
    };

    // @ts-ignore
    dispatch(useUpdateProductById(data));
    setValuePackageForm(PACKAGE_FORM.EXCISE_VALUE, undefined);
    setValuePackageForm(PACKAGE_FORM.EXCISE_RATE, undefined);
    if (isVehicle) {
      setValuePackageForm(PACKAGE_FORM.ENGINE_TYPE, undefined);
      setValuePackageForm(PACKAGE_FORM.ENGINE_POWER_HP, undefined);
      setValuePackageForm(PACKAGE_FORM.ENGINE_VOLUME_CC, undefined);
      setValuePackageForm(PACKAGE_FORM.MANUFACTURE_DATE, undefined);
    }
    setIsCompletedAdditional(false);
  }

  return (
    <>
      {isCompletedAdditional ? (<div className='add-product__notification'>
        <Completed
          info={getInfo()}
          onEdit={handleOpenModal}
          MenuComponent={MenuButton}
          caption={t('calc__text__packaging__addFieldsHsCode')}
          onRemove={handleRemove}
          isFullRow
        />
      </div>) : (<>
        <div className='add-product__notification'>
          <Notification type={NOTIFICATION_TYPE.WARNING} title={t('common__message__warning')} text={t('actions__message__fillAdditionalFieldsForHsCode')}/>
          <Button
            {...demoPlayback.fillButtonProps}
            variant='contained'
            className='add-product__addFieldButton'
            fullWidth
            startIcon={<EditIcon />}
            onClick={handleOpenModal}
          >{t('actions__text__fillAdditionalFields')}</Button>
        </div>
      </>
      )}
      {(data || isVehicle) && <ModalBig
        isOpen={openModalAddFields}
        onClose={handleCloseModal}
        width={demoPlayback.modalWidth}
        title={`${t('calc__text__packaging__addFieldsHsCode')} ${getValues(PACKAGE_FORM.CODE)}`}
        insideModal={insideModal}
      >
        <ExciseFields
          onClose={handleCloseModal}
          exciselist={data ?? []}
          product={product}
          isPrefillLoading={prefillLoading}
          prefillSuggestions={prefillSuggestions}
        />
      </ModalBig>}
    </>
  )
}

export default Excise
